import React, { useState } from 'react'
import Papa from 'papaparse'
import { FaPlus } from 'react-icons/fa'
import { Stack, SimpleModal, SimpleTabs, Button, Input, Select, Text, Heading, Checkbox, Alert, CSVChooser, Icon, FormControl } from '@/components/lib'
import utils from '../../utils/utils'
import animals from '../../utils/animals'
import colours from '../../utils/colours'
import adjectives from '../../utils/adjectives'
import api from '../../api'

export default function ParticipantCreator ({ trigger, trial, groups = [], onCreated }) {
  const [open, setOpen] = useState(false)
  const [newParticipantIsUnallocated, setNewParticipantIsUnallocated] = useState(true)
  const [newParticipantId, setNewParticipantId] = useState('')
  const [newParticipantPassword, setNewParticipantPassword] = useState('')
  const [newParticipantGroup, setNewParticipantGroup] = useState('')
  const [newParticipantCount, setNewParticipantCount] = useState('')
  const [newParticipantNameStrategy, setNewParticipantNameStrategy] = useState('')
  const [newParticipantNamePattern, setNewParticipantNamePattern] = useState('p{3p}{n}')
  const [newParticipantNameLength, setNewParticipantNameLength] = useState(5)
  const [newParticipantGroupStrategy, setNewParticipantGroupStrategy] = useState('')
  const [newParticipantGeneratePassword, setNewParticipantGeneratePassword] = useState(false)
  const [creating, setCreating] = useState(false)

  function createParticipant () {
    const data = { participantId: newParticipantId, password: newParticipantPassword || null, group: newParticipantGroup || null, isUnallocated: newParticipantIsUnallocated }
    setCreating(true)
    api.trials.createParticipant(trial._id, data, participant => {
      utils.toast('success', 'Participant registered', 'We\'ve added the participant to the trial')
      setNewParticipantId('')
      setNewParticipantPassword('')
      setCreating(false)
      onCreated([participant])
      setOpen(false)
    }, err => {
      utils.toast('error', 'Unable to create participant', err.message)
      setCreating(false)
    })
  }

  function csvFormatter (text) {
    const dat = Papa.parse(text, { skipEmptyLines: true, header: true, transform: v => v.trim() })
    return [['participantId', 'group', 'groupIds', 'password'], dat.data.map(({ participantId, group, password }) => {
      const row = { participantId, group, password }
      if (!participantId?.length) row.error = 'Invalid ID'
      if (group) {
        const splitGroups = group.split(';')
        row.groupIds = []
        splitGroups.forEach(g => {
          groups.forEach(g2 => {
            if (g2.name.toLowerCase() === g.trim().toLowerCase()) row.groupIds.push(g2._id)
          })
        })
        if (!row.groupIds) row.error = 'Invalid group name'
      }
      return row
    })]
  }

  function bulkCreateParticipants (rows) {
    rows.forEach(row => {
      row.groups = row.groupIds ?? [] // API expects groupIds to be 'groups'
      row.groupIds = undefined // API doesn't want a groupIDs param
      row.group = undefined // API doesn't want a group param
      row.isUnallocated = newParticipantIsUnallocated
    })
    setCreating(true)
    api.trials.createParticipant(trial._id, { participants: rows }, ({ participants: newlyCreated }) => {
      utils.toast('success', 'Participants registered', 'We\'ve added the participants to the trial')
      setCreating(false)
      onCreated(newlyCreated)
      setOpen(false)
    }, err => {
      utils.toast('error', 'Unable to create participants', err.message)
      setCreating(false)
    })
  }

  function bulkCreateParticipantsWithStrategy () {
    const count = parseInt(newParticipantCount)
    if (!count || count < 0 || count > 2000) {
      return utils.toast('error', 'Unable to create participants', 'Your number is invalid or too high.')
    }
    const newParticipants = []
    for (let i = 0; i < count; i++) {
      let newId
      if (newParticipantNameStrategy === 'phrase') {
        const colour = colours[Math.floor(Math.random() * colours.length)]
        const adjective = adjectives[Math.floor(Math.random() * adjectives.length)]
        const animal = animals[Math.floor(Math.random() * animals.length)]
        const phrase = `${adjective}-${colour}-${animal}`.toLowerCase()
        const pattern = newParticipantNamePattern?.toLowerCase() || '{phrase}'
        if (pattern?.indexOf('{phrase}') === -1) {
          return utils.toast('error', 'Unable to create participants', 'Your ID pattern is invalid')
        }
        newId = pattern.replace('{phrase}', phrase)
      } else if (newParticipantNameStrategy === 'sequential') {
        if (!newParticipantNamePattern || newParticipantNamePattern.indexOf('{n}') === -1) {
          return utils.toast('error', 'Unable to create participants', 'Your ID pattern is invalid')
        }
        const newParticipantNumber = trial.participantCount + i + 1
        newId = newParticipantNamePattern.toLowerCase().replace('{n}', `${newParticipantNumber}`)

        // Handle zero-padded numbers (e.g. {3p} for 001, 002, etc):
        try {
          newId = newId.replace(/\{(\d)p\}/g, (_, zeros) => '0'.repeat(parseInt(zeros) - `${newParticipantNumber}`.length))
        } catch (err) {
          return utils.toast('error', 'Unable to create participants', 'Your ID pattern is invalid or does not provide enough padding.')
        }
      } else if (newParticipantNameStrategy === 'alphanumeric') {
        const length = newParticipantNameLength || 5
        if (length < 3 || length > 10) {
          return utils.toast('error', 'Unable to create participants', 'ID length must be between 3 and 10')
        }
        newId = utils.randomAlphaNumeric(length).toUpperCase()
      } else {
        return utils.toast('error', 'Unable to create participants', 'A ID strategy must be selected')
      }
      if (newId) {
        const newParticipant = { participantId: newId }
        if (newParticipantGeneratePassword) {
          const adjective = adjectives[Math.floor(Math.random() * adjectives.length)]
          const animal = animals[Math.floor(Math.random() * animals.length)]
          newParticipant.password = `${adjective}-${animal}`.toLowerCase()
        }
        newParticipants.push(newParticipant)
      }
    }

    if (newParticipantGroupStrategy && groups?.length > 0) {
      let shuffledGroups = []
      if (newParticipantGroupStrategy === 'random') {
        // Build random list of IDs for use in random strategy
        while (shuffledGroups.length < newParticipants.length) {
          shuffledGroups.push(groups[shuffledGroups.length % groups.length]._id)
        }
        shuffledGroups = utils.shuffleArray(shuffledGroups)
      } else if (newParticipantGroupStrategy.indexOf('block-') === 0) {
        // Build random list of IDs for use in block strategy
        const blockSize = parseInt(newParticipantGroupStrategy.replace('block-', '')) * groups.length
        let counter = 0
        while (shuffledGroups.length < newParticipants.length) {
          const block = []
          while (block.length < blockSize) {
            block.push(groups[counter % groups.length]._id)
            counter++
          }
          utils.shuffleArray(block).forEach(g => shuffledGroups.push(g))
        }
      }
      newParticipants.forEach((p, i) => {
        if (newParticipantGroupStrategy === 'roundRobin') {
          p.groupIds = [groups[i % groups.length]._id]
        } else if (newParticipantGroupStrategy === 'random' || newParticipantGroupStrategy.indexOf('block-') === 0) {
          p.groupIds = [shuffledGroups[i]]
        } else { // Assume a group ID
          p.groupIds = [newParticipantGroupStrategy]
        }
      })
    }

    // Download a CSV of IDs and passwords if password is set.
    if (newParticipantGeneratePassword) {
      let csvData = 'ParticipantID,password'
      newParticipants.forEach(p => {
        csvData += `%0A${p.participantId}%2C${p.password}`
      })
      const element = document.createElement('a')
      element.setAttribute('href', `data:text/csv;charset=utf-8,${csvData}`)
      element.setAttribute('download', 'participants.csv')
      element.style.display = 'none'
      document.body.appendChild(element)
      element.click()
      document.body.removeChild(element)
    }

    // Proceed to create them on the API.
    bulkCreateParticipants(newParticipants)
  }

  return (
    <>
      {trigger
        ? React.cloneElement(trigger, { onClick: () => setOpen(true) })
        : <Button colorPalette='blue' onClick={() => setOpen(true)}><Icon as={FaPlus} mr={2} /> Create participants</Button>}
      <SimpleModal
        isOpen={open} onClose={() => setOpen(false)} modalProps={{ size: 'lg' }}
        header='Register participants'
        body={
          <Stack gap={5}>
            <Checkbox isChecked={newParticipantIsUnallocated} onChange={e => setNewParticipantIsUnallocated(e.checked)}>
              <Text fontWeight='bold'>Mark new participants as "unallocated"</Text>
              <Text fontSize='sm'>Unallocated participants can be used as part of automations, such as during the screening process.</Text>
            </Checkbox>
            <SimpleTabs
              tabs={[
                {
                  title: 'Single',
                  content: (
                    <Stack gap={5}>
                      <Heading size='sm'>Create a new participant ID that can be used to join the trial</Heading>
                      <FormControl label='Participant ID'>
                        <Input placeholder='p1' value={newParticipantId} onChange={e => setNewParticipantId(e.target.value)} />
                      </FormControl>
                      <FormControl label='Participant password (optional)' helperText='If not set the trial password will be used. If set, make a note of it since Trialflare will not be able to reveal it later.'>
                        <Input placeholder='Type a password...' type='password' value={newParticipantPassword} onChange={e => setNewParticipantPassword(e.target.value)} />
                      </FormControl>
                      {groups.length > 0 &&
                        <FormControl label='Participant group (optional)' helperText='If not set, the participant will not be assigned to a group.'>
                          <Select label='Participant group (optional)' value={newParticipantGroup} onChange={e => setNewParticipantGroup(e.target.value)} placeholder='Choose a group'>
                            {groups.map(g => <option key={g._id} value={g._id}>{g.name}</option>)}
                          </Select>
                        </FormControl>}
                      <Button isLoading={creating} colorPalette='blue' onClick={createParticipant}>Create participant</Button>
                    </Stack>)
                },
                {
                  title: 'Multiple',
                  content: (
                    <Stack gap={5}>
                      <Heading size='sm'>Trialflare can create several participants for you in one go</Heading>
                      <FormControl label='Number of participants to create'>
                        <Input placeholder='0' value={newParticipantCount} onChange={e => setNewParticipantCount(e.target.value.replace(/[^\d]/g, ''))} />
                      </FormControl>

                      <Stack p={2} bg='bg.surface'>
                        <FormControl label='ID naming strategy'>
                          <Select
                            value={newParticipantNameStrategy} onChange={e => {
                              setNewParticipantNameStrategy(e.target.value)
                              setNewParticipantNamePattern(e.target.value === 'phrase' ? '{phrase}' : 'p{3p}{n}')
                            }} placeholder='Select a strategy...'
                          >
                            <option value='phrase'>Random three-word phrase</option>
                            <option value='sequential'>Sequential pattern</option>
                            <option value='alphanumeric'>Random sequence of alphanumeric characters</option>
                          </Select>
                        </FormControl>

                        {newParticipantNameStrategy === 'phrase' &&
                          <FormControl label='Phrase naming pattern' helperText='"{phrase}" will be replaced with the generated phrase.'>
                            <Input value={newParticipantNamePattern} onChange={e => setNewParticipantNamePattern(e.target.value.replace(/\s/g, ''))} />
                          </FormControl>}

                        {newParticipantNameStrategy === 'sequential' &&
                          <FormControl label='Sequential naming pattern' helperText='"{n}" will be replaced with a sequential participant number. "{3p}" (etc.) will pad the number with leading zeros to 3 digits.'>
                            <Input value={newParticipantNamePattern} onChange={e => setNewParticipantNamePattern(e.target.value.replace(/\s/g, ''))} />
                          </FormControl>}

                        {newParticipantNameStrategy === 'alphanumeric' &&
                          <FormControl label='ID length' helperText='The number of characters to generate for each ID (default: 5)'>
                            <Input placeholder='5' value={newParticipantNameLength} onChange={e => setNewParticipantNameLength(parseInt(e.target.value) || '')} />
                          </FormControl>}
                      </Stack>

                      {groups?.length > 0 &&
                        <Stack p={2} bg='bg.surface'>
                          <FormControl label='Grouping strategy'>
                            <Select
                              value={newParticipantGroupStrategy} onChange={e => {
                                setNewParticipantGroupStrategy(e.target.value)
                              }} placeholder='Select a strategy...'
                            >
                              {groups.map(group =>
                                <option key={group._id} value={group._id}>Add all to {group.name}</option>
                              )}
                              <option value='roundRobin'>Round robin ("A", "B", "C", "A", "B", ...)</option>
                              <option value='random'>Random uniform allocation</option>
                              <option value='block-1'>Random blocks of {groups.length * 1}</option>
                              <option value='block-2'>Random blocks of {groups.length * 2}</option>
                              <option value='block-3'>Random blocks of {groups.length * 3}</option>
                              <option value='block-4'>Random blocks of {groups.length * 4}</option>
                              <option value='block-5'>Random blocks of {groups.length * 5}</option>
                            </Select>
                          </FormControl>
                        </Stack>}

                      <Stack p={2} bg='bg.surface'>
                        <Checkbox isChecked={newParticipantGeneratePassword} onChange={e => setNewParticipantGeneratePassword(e.checked)}>
                          <Text>Generate random passwords</Text>
                          <Text><small>Optional. If not selected, the trial password, if set, will be in effect.</small></Text>
                        </Checkbox>

                        {newParticipantGeneratePassword && <Alert status='info' variant='left-accent' fontSize='sm'>A CSV file containing participant data will be downloaded automatically for you. Keep this safe, as Trialflare will not be able to reveal participant passwords again later.</Alert>}
                      </Stack>

                      <Text>Please note that duplicate participant IDs will be ignored.</Text>

                      <Button isLoading={creating} colorPalette='blue' onClick={bulkCreateParticipantsWithStrategy}>Create {newParticipantCount ?? ''} participants</Button>
                    </Stack>)
                },
                {
                  title: 'CSV upload',
                  content: (
                    <Stack gap={5}>
                      <Heading size='sm'>Upload a CSV file to create several participants in one go</Heading>
                      <CSVChooser
                        trigger={<Button colorPalette='blue'>Get started</Button>}
                        title='Bulk register participants for this trial'
                        instructions='Upload a CSV file to create many participants in one go. Your CSV must include a "participantId" and may also include "group" and "password" columns. Please note that duplicate participant IDs will be ignored. Please note: It is important that you keep a record of any passwords if used.'
                        formatFunction={csvFormatter}
                        onComplete={bulkCreateParticipants}
                      />
                    </Stack>)
                }
              ]}
            />
          </Stack>
}
      />
    </>
  )
}
